import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SubscriptionNote,
  SubscriptionPeriod,
} from '../../../models/CompaniesModel';
import Button from '../../Button/Button';
import Modal from '../../Modal/Modal';
import './CanoneSection.scss';

const uuid = () =>
  // RFC4122-ish; non serve crypto-grade — è solo l'id locale del periodo/nota
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

const isExpired = (p: SubscriptionPeriod) =>
  Date.now() >= new Date(p.endDate).getTime();

const isExpiringSoon = (p: SubscriptionPeriod, days = 60) => {
  const end = new Date(p.endDate).getTime();
  const limit = Date.now() + days * 24 * 60 * 60 * 1000;
  return end > Date.now() && end <= limit;
};

const formatDate = (d: string) =>
  d ? new Date(d).toLocaleDateString('it-IT') : '';

/** Calcola la durata in mesi (frazionari) fra due date ISO. Ritorna null se le date non sono valide. */
const computeMonths = (startISO?: string, endISO?: string): number | null => {
  if (!startISO || !endISO) return null;
  const s = new Date(startISO).getTime();
  const e = new Date(endISO).getTime();
  if (Number.isNaN(s) || Number.isNaN(e) || e <= s) return null;
  // 30.4375 = media giorni/mese, sufficiente per il canone (si arrotonda a 2 decimali)
  return Math.round(((e - s) / (1000 * 60 * 60 * 24 * 30.4375)) * 100) / 100;
};

const computeAmountPerSeat = (
  totalAmount?: number,
  months?: number | null,
  seats?: number,
): number | null => {
  if (!totalAmount || !months || !seats) return null;
  return Math.round((totalAmount / months / seats) * 100) / 100;
};

interface Props {
  subscriptions: SubscriptionPeriod[];
  ccEmails: string[];
  onChange: (next: {
    subscriptions: SubscriptionPeriod[];
    ccEmails: string[];
  }) => void;
}

const CanoneSection: React.FC<Props> = ({
  subscriptions,
  ccEmails,
  onChange,
}) => {
  const { t } = useTranslation();

  const sortedPeriods = useMemo(
    () =>
      [...subscriptions].sort(
        (a, b) =>
          new Date(b.startDate).getTime() - new Date(a.startDate).getTime(),
      ),
    [subscriptions],
  );

  const [editingPeriod, setEditingPeriod] = useState<SubscriptionPeriod | null>(
    null,
  );
  const [isCreating, setIsCreating] = useState(false);
  const [draft, setDraft] = useState<Partial<SubscriptionPeriod>>({});
  const [draftNote, setDraftNote] = useState('');
  const [ccInput, setCcInput] = useState('');

  const startCreate = () => {
    setDraft({
      startDate: '',
      endDate: '',
      totalAmount: undefined,
      months: undefined,
      amountPerSeat: undefined,
      seats: undefined,
      notes: [],
    });
    setIsCreating(true);
  };

  const startEdit = (p: SubscriptionPeriod) => {
    setEditingPeriod(p);
    setDraft({ ...p });
  };

  const closeModal = () => {
    setIsCreating(false);
    setEditingPeriod(null);
    setDraft({});
    setDraftNote('');
  };

  const persistDraft = () => {
    if (!draft.startDate || !draft.endDate) return;
    const totalAmount = Number(draft.totalAmount ?? 0);
    const seats = Number(draft.seats ?? 0);
    // Se l'utente non ha valorizzato mesi/importo a utenza, derivali al salvataggio
    // così restano persistenti (utile per export e logica di reminder).
    const monthsFinal =
      draft.months !== undefined && draft.months !== null
        ? Number(draft.months)
        : computeMonths(draft.startDate as string, draft.endDate as string) ?? undefined;
    const amountPerSeatFinal =
      draft.amountPerSeat !== undefined && draft.amountPerSeat !== null
        ? Number(draft.amountPerSeat)
        : computeAmountPerSeat(totalAmount, monthsFinal, seats) ?? undefined;
    const cleaned: SubscriptionPeriod = {
      id: editingPeriod?.id ?? uuid(),
      startDate: draft.startDate as string,
      endDate: draft.endDate as string,
      totalAmount,
      months: monthsFinal,
      amountPerSeat: amountPerSeatFinal,
      seats,
      notes: draft.notes ?? [],
      reminderSentAt: editingPeriod?.reminderSentAt,
      createdAt: editingPeriod?.createdAt ?? new Date().toISOString(),
    };
    const next = editingPeriod
      ? subscriptions.map((p) => (p.id === editingPeriod.id ? cleaned : p))
      : [...subscriptions, cleaned];
    onChange({ subscriptions: next, ccEmails });
    closeModal();
  };

  const addDraftNote = () => {
    const text = draftNote.trim();
    if (!text) return;
    const note: SubscriptionNote = {
      id: uuid(),
      text,
      createdAt: new Date().toISOString(),
    };
    setDraft((d) => ({ ...d, notes: [...(d.notes ?? []), note] }));
    setDraftNote('');
  };

  const removePeriod = (id: string) => {
    onChange({
      subscriptions: subscriptions.filter((p) => p.id !== id),
      ccEmails,
    });
  };

  const addCcEmail = () => {
    const email = ccInput.trim();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    if (ccEmails.includes(email)) {
      setCcInput('');
      return;
    }
    onChange({ subscriptions, ccEmails: [...ccEmails, email] });
    setCcInput('');
  };

  const removeCcEmail = (email: string) => {
    onChange({
      subscriptions,
      ccEmails: ccEmails.filter((e) => e !== email),
    });
  };

  return (
    <div className="canone-section">
      <div className="canone-header">
        <div>
          <h3>{t('createAgency.canone.title')}</h3>
          <p className="hint">{t('createAgency.canone.description')}</p>
        </div>
        <Button onClick={startCreate}>
          + {t('createAgency.canone.addPeriod')}
        </Button>
      </div>

      <div className="cc-row">
        <label>{t('createAgency.canone.ccEmails.label')}</label>
        <p className="hint">{t('createAgency.canone.ccEmails.hint')}</p>
        <div className="cc-input-row">
          <input
            type="email"
            value={ccInput}
            placeholder={
              t('createAgency.canone.ccEmails.placeholder') as string
            }
            onChange={(e) => setCcInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addCcEmail();
              }
            }}
          />
          <Button onClick={addCcEmail} outline>
            {t('createAgency.canone.ccEmails.add')}
          </Button>
        </div>
        {ccEmails.length > 0 && (
          <div className="cc-tags">
            {ccEmails.map((email) => (
              <span className="cc-tag" key={email}>
                {email}
                <button
                  type="button"
                  className="cc-tag-remove"
                  onClick={() => removeCcEmail(email)}
                  aria-label="rimuovi"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="canone-table-wrapper">
        <table className="canone-table">
          <thead>
            <tr>
              <th>{t('createAgency.canone.table.startDate')}</th>
              <th>{t('createAgency.canone.table.endDate')}</th>
              <th>{t('createAgency.canone.table.totalAmount')}</th>
              <th>{t('createAgency.canone.table.months')}</th>
              <th>{t('createAgency.canone.table.seats')}</th>
              <th>{t('createAgency.canone.table.amountPerSeat')}</th>
              <th>{t('createAgency.canone.table.notesCount')}</th>
              <th>{t('createAgency.canone.table.status')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sortedPeriods.length === 0 && (
              <tr>
                <td colSpan={9} className="empty-row">
                  {t('createAgency.canone.empty')}
                </td>
              </tr>
            )}
            {sortedPeriods.map((p) => (
              <tr key={p.id}>
                <td>{formatDate(p.startDate)}</td>
                <td>{formatDate(p.endDate)}</td>
                <td>{(p.totalAmount ?? 0).toLocaleString('it-IT')} €</td>
                <td>{p.months ?? computeMonths(p.startDate, p.endDate) ?? '—'}</td>
                <td>{p.seats}</td>
                <td>
                  {(
                    p.amountPerSeat ??
                    computeAmountPerSeat(
                      p.totalAmount,
                      p.months ?? computeMonths(p.startDate, p.endDate),
                      p.seats,
                    )
                  )?.toLocaleString('it-IT') ?? '—'}{' '}
                  €
                </td>
                <td className="notes-cell">
                  {(() => {
                    const notes = p.notes ?? [];
                    if (!notes.length) return <span className="muted">0</span>;
                    const sorted = [...notes].sort(
                      (a, b) =>
                        new Date(b.createdAt).getTime() -
                        new Date(a.createdAt).getTime(),
                    );
                    const last = sorted[0];
                    const tooltip = sorted
                      .map((n) => `[${formatDate(n.createdAt)}] ${n.text}`)
                      .join('\n');
                    return (
                      <span className="notes-summary" title={tooltip}>
                        <strong>{notes.length}</strong>
                        <span className="notes-last"> — “{last.text}”</span>
                      </span>
                    );
                  })()}
                </td>
                <td>
                  {isExpired(p) ? (
                    <span className="status-pill status-expired">
                      {t('createAgency.canone.status.expired')}
                    </span>
                  ) : isExpiringSoon(p) ? (
                    <span className="status-pill status-soon">
                      {t('createAgency.canone.status.expiringSoon')}
                    </span>
                  ) : (
                    <span className="status-pill status-active">
                      {t('createAgency.canone.status.active')}
                    </span>
                  )}
                </td>
                <td className="row-actions">
                  <button
                    type="button"
                    className="row-btn"
                    onClick={() => startEdit(p)}
                  >
                    {t('createAgency.canone.editPeriod')}
                  </button>
                  <button
                    type="button"
                    className="row-btn danger"
                    onClick={() => removePeriod(p.id)}
                  >
                    {t('createAgency.canone.removePeriod')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(isCreating || editingPeriod) && (
        <Modal open close={closeModal} classWrapper="canone-modal-wrapper">
          <div className="canone-modal">
            <h2>
              {editingPeriod
                ? t('createAgency.canone.modal.editTitle')
                : t('createAgency.canone.modal.createTitle')}
            </h2>

            <div className="modal-grid">
              <div className="form-group">
                <label className="form-control-label">
                  {t('createAgency.canone.table.startDate')}
                </label>
                <input
                  type="date"
                  className="form-control p-md"
                  value={(draft.startDate as string) ?? ''}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, startDate: e.target.value }))
                  }
                />
              </div>
              <div className="form-group">
                <label className="form-control-label">
                  {t('createAgency.canone.table.endDate')}
                </label>
                <input
                  type="date"
                  className="form-control p-md"
                  value={(draft.endDate as string) ?? ''}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, endDate: e.target.value }))
                  }
                />
              </div>
              <div className="form-group">
                <label className="form-control-label">
                  {t('createAgency.canone.table.totalAmount')}
                </label>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  className="form-control p-md"
                  placeholder="0,00"
                  value={
                    draft.totalAmount !== undefined
                      ? String(draft.totalAmount)
                      : ''
                  }
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      totalAmount: e.target.value === '' ? undefined : Number(e.target.value),
                    }))
                  }
                />
              </div>
              <div className="form-group">
                <label className="form-control-label">
                  {t('createAgency.canone.table.seats')}
                </label>
                <input
                  type="number"
                  min={0}
                  step={1}
                  className="form-control p-md"
                  placeholder="0"
                  value={
                    draft.seats !== undefined ? String(draft.seats) : ''
                  }
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      seats: e.target.value === '' ? undefined : Number(e.target.value),
                    }))
                  }
                />
              </div>
              {(() => {
                const computedMonths = computeMonths(
                  draft.startDate as string,
                  draft.endDate as string,
                );
                const monthsForCalc =
                  draft.months !== undefined && draft.months !== null
                    ? Number(draft.months)
                    : computedMonths;
                const computedAmountPerSeat = computeAmountPerSeat(
                  draft.totalAmount,
                  monthsForCalc,
                  draft.seats,
                );
                return (
                  <>
                    <div className="form-group">
                      <label className="form-control-label">
                        {t('createAgency.canone.table.months')}
                      </label>
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        className="form-control p-md"
                        placeholder={
                          computedMonths !== null
                            ? `auto: ${computedMonths}`
                            : '0'
                        }
                        value={
                          draft.months !== undefined && draft.months !== null
                            ? String(draft.months)
                            : ''
                        }
                        onChange={(e) =>
                          setDraft((d) => ({
                            ...d,
                            months:
                              e.target.value === ''
                                ? undefined
                                : Number(e.target.value),
                          }))
                        }
                      />
                      <p className="hint">
                        {t('createAgency.canone.modal.monthsHint')}
                      </p>
                    </div>
                    <div className="form-group">
                      <label className="form-control-label">
                        {t('createAgency.canone.table.amountPerSeat')}
                      </label>
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        className="form-control p-md"
                        placeholder={
                          computedAmountPerSeat !== null
                            ? `auto: ${computedAmountPerSeat}`
                            : '0,00'
                        }
                        value={
                          draft.amountPerSeat !== undefined &&
                          draft.amountPerSeat !== null
                            ? String(draft.amountPerSeat)
                            : ''
                        }
                        onChange={(e) =>
                          setDraft((d) => ({
                            ...d,
                            amountPerSeat:
                              e.target.value === ''
                                ? undefined
                                : Number(e.target.value),
                          }))
                        }
                      />
                      <p className="hint">
                        {t('createAgency.canone.modal.amountPerSeatHint')}
                      </p>
                    </div>
                  </>
                );
              })()}
            </div>

            <div className="notes-section">
              <h3>{t('createAgency.canone.modal.notesTitle')}</h3>
              <p className="hint">
                {t('createAgency.canone.modal.notesHint')}
              </p>
              <div className="notes-list">
                {(draft.notes ?? []).map((n) => (
                  <div key={n.id} className="note-item">
                    <div className="note-meta">{formatDate(n.createdAt)}</div>
                    <div className="note-text">{n.text}</div>
                  </div>
                ))}
                {(draft.notes ?? []).length === 0 && (
                  <div className="empty-row">
                    {t('createAgency.canone.modal.notesEmpty')}
                  </div>
                )}
              </div>
              <div className="note-input-row">
                <textarea
                  className="form-control p-md note-textarea"
                  value={draftNote}
                  rows={2}
                  placeholder={
                    t('createAgency.canone.modal.notesPlaceholder') as string
                  }
                  onChange={(e) => setDraftNote(e.target.value)}
                />
                <Button onClick={addDraftNote} outline>
                  {t('createAgency.canone.modal.addNote')}
                </Button>
              </div>
            </div>

            <div className="modal-actions">
              <Button onClick={closeModal} outline>
                {t('home.companies.modal.cancel')}
              </Button>
              <Button onClick={persistDraft}>
                {t('home.companies.modal.confirm')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default CanoneSection;